import * as React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Header from "../components/common/header/Header";
import { Article } from "../interfaces/Article";
import Stack from "@mui/material/Stack";
import Footer from "../components/common/Footer/Footer";
import Pagination from "@mui/material/Pagination";
import theme from "../theme";
import { Typography, Breadcrumbs, Container } from "@mui/material";
import { getLocalProductsByBrand } from "../services/core-api";
import LocalBrandProductItem from "../components/products/LocalBrandProductItem";

interface ProductsResponse {
   items: Article[];
   total: number;
   page: number;
   pages: number;
}

const LocalProductSearchByBrandPage = () => {
   const [articles, setArticles] = React.useState<Article[]>([]);
   const [brandId, setBrandId] = React.useState(0);
   const [page, setPage] = React.useState(1);
   const [count, setCount] = React.useState(0);
   const [rowsPerPage] = React.useState(20);
   const [isLoading, setIsLoading] = React.useState(true);
   const [isWrongPath, setIsWrongPath] = React.useState(false);

   const isFirstRun = React.useRef(true);

   React.useEffect(() => {
      if (isFirstRun.current) {
         isFirstRun.current = false;
         const urlParams = new URLSearchParams(window.location.search);
         const brandIdParam = urlParams.get("brand");

         if (brandIdParam == null) {
            setTimeout(() => {
               setIsWrongPath(true);
               setIsLoading(false);
            }, 1000);
         }

         if (brandIdParam) {
            const brandIdUrlParam = parseInt(brandIdParam, 10);
            setBrandId(brandIdUrlParam);
         }
      }
   }, []);

   React.useEffect(() => {
      if (brandId) {
         (async () => {
            try {
               setIsLoading(true);
               const options = {
                  page: page,
                  brand: brandId
               };
               const response = await getLocalProductsByBrand(options);
               if (response) {
                  setCount(Math.ceil(response.total / rowsPerPage));
                  setArticles(response.items);
               }
            } catch (error) {
               console.error('Error fetching products:', error);
            } finally {
               setIsLoading(false);
            }
         })();
      }
   }, [brandId, page, rowsPerPage]);

   const handlePageChange = (
      event: React.ChangeEvent<unknown>,
      newPage: number
   ) => {
      setPage(newPage);
      window.scrollTo({
         top: 100,
         left: 100,
         behavior: "smooth",
      });
   };

   // Render loading skeleton
   const renderSkeleton = React.useCallback(() => (
      <Stack spacing={2}>
         {[...Array(5)].map((_, index) => (
            <Box key={index} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
               <Typography component="div" variant="h6" sx={{ bgcolor: '#e0e0e0', width: '60%', height: 20 }} />
               <Typography component="div" sx={{ bgcolor: '#e0e0e0', width: '40%', height: 20, mt: 1 }} />
            </Box>
         ))}
      </Stack>
   ), []);

   // Render no data state
   const renderNoData = React.useCallback(() => (
      <Box sx={{
         display: "flex",
         flexDirection: "column",
         justifyContent: "center",
         alignItems: "center",
         height: "50vh",
      }}>
         <img
            style={{ width: "100px", height: "auto" }}
            src="./no_data.png"
            alt="No data"
         />
         <Typography
            variant="h6"
            sx={{
               color: theme.palette.secondary.dark,
               fontFamily: "Inter",
               fontWeight: "normal",
               mt: 2
            }}
         >
            No Data!
         </Typography>
      </Box>
   ), []);

   return (
      <>
         <Header />
         <Box sx={{
            flexGrow: 1,
            m: 2,
            minHeight: '71vh',
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
         }}>
            <Container maxWidth="lg">
               <Grid container spacing={3}>
                  <Grid item xs={12} sm={12}>
                     <Box sx={{
                        bgcolor: "#f1f1f1",
                        paddingTop: '10px',
                        paddingBottom: '10px',
                        paddingLeft: 1,
                        borderTop: `2px solid ${theme.palette.primary.dark}`,
                     }}>
                        <Grid container spacing={1}>
                           <Grid item xs={12} sm={12} md={5} sx={{ paddingLeft: '0px !important' }}>
                              <Stack spacing={2} sx={{ paddingTop: '3px', paddingLeft: '5px' }}>
                                 <Breadcrumbs aria-label="breadcrumb">
                                    <Typography
                                       sx={{
                                          color: theme.palette.text.primary,
                                          fontWeight: 600,
                                          fontSize: "15px",
                                          fontFamily: "Inter",
                                       }}
                                    >
                                       Products
                                    </Typography>
                                 </Breadcrumbs>
                              </Stack>
                           </Grid>
                        </Grid>
                     </Box>
                     <Box sx={{ mt: 4 }}>
                        {isLoading ? (
                           renderSkeleton()
                        ) : isWrongPath ? (
                           renderNoData()
                        ) : articles.length === 0 ? (
                           <Typography variant="h5" textAlign="center" mt={10}>
                              No products found!
                           </Typography>
                        ) : (
                           <>
                              {articles.map((article, index) => (
                                 <LocalBrandProductItem key={article.articleNumber} article={article} index={index + 1} />
                              ))}
                           </>
                        )}
                     </Box>
                  </Grid>
               </Grid>
            </Container>
            {!isLoading && !isWrongPath && articles.length > 0 && (
               <Stack spacing={2} sx={{ pt: 2, pb: 2 }}>
                  <Pagination
                     count={count}
                     page={page}
                     variant="outlined"
                     shape="rounded"
                     onChange={handlePageChange}
                  />
               </Stack>
            )}
         </Box>
         <Footer />
      </>
   );
};

export default LocalProductSearchByBrandPage;